import { deleteMemoryAction } from '@/app/actions/memory/delete';
import { saveToMemoryAction } from '@/app/actions/memory/save';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tags,
  TagsContent,
  TagsEmpty,
  TagsItem,
  TagsList,
  TagsTrigger,
  TagsValue,
} from '@/components/ui/kibo-ui/tags';
import { Label } from '@/components/ui/label';
import { useAnalytics } from '@/hooks/use-analytics';
import { handleError } from '@/lib/error/handle';
import {
  getDescriptionsFromImageNodes,
  getTextFromTextNodes,
  getTranscriptionFromAudioNodes,
  getTweetContentFromTweetNodes,
} from '@/lib/xyflow';
import { useProject } from '@/providers/project';
import { type MemoryItem, useMemoryStore } from '@/stores/memory';
import { getIncomers, useReactFlow } from '@xyflow/react';
import {
  CheckIcon,
  ClockIcon,
  DatabaseIcon,
  Loader2Icon,
  RotateCcwIcon,
  SaveIcon,
  TextSearchIcon,
  TrashIcon,
} from 'lucide-react';
import { type ComponentProps, useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { mutate } from 'swr';
import type { MemoryNodeProps } from '.';
import { NodeLayout } from '../layout';
import { MemorySearchModal } from './memory-search-modal';

type MemoryTransformProps = MemoryNodeProps & {
  title: string;
};

const PRESET_TAGS = [
  'notes',
  'research',
  'ideas',
  'documentation',
  'meeting',
  'project',
  'personal',
  'work',
  'important',
  'draft',
  'reference',
  'todo',
  'creative',
  'learning',
  'archive',
];

export const MemoryTransform = ({
  data,
  id,
  type,
  title,
}: MemoryTransformProps) => {
  const { updateNodeData, getNodes, getEdges } = useReactFlow();
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showMemorySearch, setShowMemorySearch] = useState(false);
  const project = useProject();
  const analytics = useAnalytics();
  const { addMemory, removeMemory } = useMemoryStore();

  const handleTagSelect = (tag: string) => {
    const currentTags = data.tags || [];
    if (!currentTags.includes(tag)) {
      updateNodeData(id, { tags: [...currentTags, tag] });
    }
  };

  const handleTagRemove = (tagToRemove: string) => {
    const currentTags = data.tags || [];
    updateNodeData(id, {
      tags: currentTags.filter((tag) => tag !== tagToRemove),
    });
  };

  const handleSaveToMemory = useCallback(async () => {
    if (loading || !project?.id) {
      return;
    }

    try {
      const incomers = getIncomers({ id }, getNodes(), getEdges());

      // Collect content from various node types
      const textContent = getTextFromTextNodes(incomers);
      const imageDescriptions = getDescriptionsFromImageNodes(incomers);
      const audioTranscriptions = getTranscriptionFromAudioNodes(incomers);
      const tweetContent = getTweetContentFromTweetNodes(incomers);

      // Combine all content
      const allContent = [
        ...textContent,
        ...imageDescriptions,
        ...audioTranscriptions,
        ...tweetContent,
      ].filter(Boolean);

      if (!allContent.length && !data.content) {
        throw new Error('No content found to save to memory');
      }

      const contentToSave = data.content || allContent.join('\n\n');

      setLoading(true);

      analytics.track('canvas', 'node', 'generate', {
        type: 'memory',
        contentLength: contentToSave.length,
        hasContainerTags: !!data.tags?.length,
        incomingNodeCount: incomers.length,
      });

      const response = await saveToMemoryAction({
        content: contentToSave,
        nodeId: id,
        projectId: project.id,
        tags: data.tags,
        incomingNodes: incomers.map((node) => ({
          id: node.id,
          type: node.type || 'unknown',
          data: node.data || {},
        })),
      });

      if ('error' in response) {
        throw new Error(response.error);
      }

      updateNodeData(id, response.nodeData);

      // Add the new memory to the store
      const nodeData = response.nodeData as {
        generated?: { memoryId?: string; autoMetadata?: unknown };
      };
      if (nodeData.generated?.memoryId) {
        const newMemory: MemoryItem = {
          id: nodeData.generated.memoryId,
          content: contentToSave,
          title: data.tags?.[0] || 'Memory',
          metadata: nodeData.generated.autoMetadata as Record<string, unknown>,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        addMemory(newMemory);
      }

      toast.success('Content saved to memory successfully');

      setTimeout(() => mutate('credits'), 5000);
    } catch (error) {
      handleError('Error saving to memory', error);
    } finally {
      setLoading(false);
    }
  }, [
    loading,
    project?.id,
    id,
    getNodes,
    getEdges,
    data.content,
    data.tags,
    analytics,
    updateNodeData,
    addMemory,
  ]);

  const handleDeleteMemory = useCallback(async () => {
    if (!data.generated?.memoryId || deleting) {
      return;
    }

    setDeleting(true);
    try {
      const response = await deleteMemoryAction({
        memoryId: data.generated.memoryId,
      });

      if ('error' in response) {
        throw new Error(response.error);
      }

      // Remove from store
      removeMemory(data.generated.memoryId);

      // Clear the memory data from the node
      updateNodeData(id, {
        generated: {
          ...data.generated,
          memoryId: undefined,
          status: undefined,
          result: undefined,
        },
      });

      toast.success('Memory deleted successfully');
    } catch (error) {
      handleError('Error deleting memory', error);
    } finally {
      setDeleting(false);
    }
  }, [data.generated, deleting, updateNodeData, id, removeMemory]);

  const handleMemorySelect = useCallback(
    (memory: MemoryItem) => {
      // Get content from available fields
      const memoryContent =
        memory.content || memory.summary || memory.title || '';

      // Update the node with the selected memory content
      updateNodeData(id, {
        content: memoryContent,
        generated: {
          ...data.generated,
          memoryId: memory.id,
          status: 'loaded',
          result: `Loaded memory: ${memory.id}`,
        },
      });
      toast.success('Memory loaded into node');
    },
    [updateNodeData, id, data.generated]
  );

  const toolbar = useMemo<ComponentProps<typeof NodeLayout>['toolbar']>(() => {
    const items: ComponentProps<typeof NodeLayout>['toolbar'] = [];

    if (loading) {
      items.push({
        tooltip: 'Saving to memory...',
        children: (
          <Button
            size="icon"
            className="rounded-full"
            disabled
            variant="memory"
          >
            <Loader2Icon className="animate-spin" size={12} />
          </Button>
        ),
      });
    } else if (data.generated?.memoryId) {
      items.push({
        tooltip: 'Overwrite memory',
        children: (
          <Button
            size="icon"
            className="rounded-full"
            onClick={handleSaveToMemory}
            disabled={loading || !project?.id}
            variant="memory"
          >
            <RotateCcwIcon size={12} />
          </Button>
        ),
      });
    } else {
      items.push({
        tooltip: 'Save to memory',
        children: (
          <Button
            size="icon"
            className="rounded-full"
            onClick={handleSaveToMemory}
            disabled={loading || !project?.id}
            variant="memory"
          >
            <SaveIcon size={12} />
          </Button>
        ),
      });
    }

    // Add a button to manage the memories
    items.push({
      tooltip: 'Search memories',
      children: (
        <Button
          size="icon"
          className="rounded-full"
          onClick={() => setShowMemorySearch(true)}
          disabled={!project?.id}
          variant="outline"
        >
          <TextSearchIcon size={12} />
        </Button>
      ),
    });

    // Add a button to delete the memory (only if memory exists)
    if (data.generated?.memoryId) {
      items.push({
        tooltip: deleting ? 'Deleting memory...' : 'Delete memory',
        children: (
          <Button
            size="icon"
            className="rounded-full"
            onClick={handleDeleteMemory}
            disabled={deleting}
            variant="destructive"
          >
            {deleting ? (
              <Loader2Icon className="animate-spin" size={12} />
            ) : (
              <TrashIcon size={12} />
            )}
          </Button>
        ),
      });
    }

    return items;
  }, [
    loading,
    deleting,
    data.generated?.memoryId,
    handleSaveToMemory,
    handleDeleteMemory,
    project?.id,
  ]);

  const incomers = getIncomers({ id }, getNodes(), getEdges());
  const hasIncomingContent = incomers.length > 0;

  const availableTags = PRESET_TAGS.filter(
    (tag) => !(data.tags || []).includes(tag)
  );

  return (
    <>
      <NodeLayout
        className="node-memory"
        id={id}
        data={data}
        title={title}
        type={type}
        toolbar={toolbar}
      >
        <div className="space-y-4 p-2">
          {hasIncomingContent && (
            <div className="rounded-lg bg-sky-50 p-3 dark:bg-sky-950/20">
              <div className="flex items-center gap-2 text-sky-700 text-sm dark:text-sky-300">
                <DatabaseIcon size={16} />
                <span>Content from {incomers.length} connected node(s)</span>
              </div>
            </div>
          )}

          {data.generated?.status && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {data.generated.status === 'saved' ||
                data.generated.status === 'completed' ? (
                  <CheckIcon size={16} className="text-green-500" />
                ) : data.generated.status === 'error' ? (
                  <ClockIcon size={16} className="text-red-500" />
                ) : (
                  <CheckIcon size={16} className="text-green-500" />
                )}
                <Badge
                  variant={
                    data.generated.status === 'saved' ||
                    data.generated.status === 'completed'
                      ? 'default'
                      : data.generated.status === 'error'
                        ? 'destructive'
                        : 'secondary'
                  }
                >
                  {data.generated.status === 'loaded' ? 'Loaded' : 'Active'}
                </Badge>
              </div>

              {data.generated.error && (
                <p className="text-red-600 text-sm dark:text-red-400">
                  {data.generated.error}
                </p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label className="font-medium text-sm">Tags (optional)</Label>

            <Tags>
              <TagsTrigger>
                {data.tags?.map((tag) => (
                  <TagsValue
                    key={tag}
                    onRemove={() => handleTagRemove(tag)}
                    className="bg-sky-50 text-sky-600 hover:bg-sky-100 dark:bg-sky-900/20 dark:text-sky-400 dark:hover:bg-sky-900/30"
                  >
                    {tag}
                  </TagsValue>
                ))}
              </TagsTrigger>
              <TagsContent>
                <TagsList>
                  <TagsEmpty>No tags found.</TagsEmpty>
                  {availableTags.map((tag) => (
                    <TagsItem key={tag} onSelect={() => handleTagSelect(tag)}>
                      {tag}
                    </TagsItem>
                  ))}
                </TagsList>
              </TagsContent>
            </Tags>

            <p className="text-muted-foreground text-xs">
              Select from preset tags to organize your memories
            </p>
          </div>
        </div>
      </NodeLayout>

      <MemorySearchModal
        isOpen={showMemorySearch}
        onClose={() => setShowMemorySearch(false)}
        onMemorySelect={handleMemorySelect}
      />
    </>
  );
};
